<template>
  <div class="feedback-modal-wrapper">
    <!-- Backdrop overlay -->
    <Transition name="fade">
      <div v-if="isOpen" class="feedback-backdrop" @click="closeModal"></div>
    </Transition>

    <!-- Modal body -->
    <Transition name="slide-up">
      <div v-if="isOpen" class="feedback-modal">
        <div class="feedback-modal-header">
          <h2>Send Feedback</h2>
          <button class="close-button" @click="closeModal" aria-label="Close feedback form">×</button>
        </div>

        <div class="feedback-modal-body">
          <form @submit.prevent="submitFeedback">
            <!-- Category -->
            <div class="form-group">
              <label for="feedback-category">Category</label>
              <select id="feedback-category" v-model="feedback.category" required>
                <option value="general">General Feedback</option>
                <option value="bug">Bug Report</option>
                <option value="feature-request">Feature Request</option>
                <option value="improvement">Improvement Suggestion</option>
                <option value="other">Other</option>
              </select>
            </div>

            <!-- Rating -->
            <div class="form-group">
              <label>Rating</label>
              <div class="rating-container">
                <div 
                  v-for="star in 5" 
                  :key="star" 
                  class="star" 
                  :class="{ 'active': star <= feedback.rating }"
                  @click="feedback.rating = star"
                >
                  ★
                </div>
              </div>
            </div>

            <!-- Message -->
            <div class="form-group">
              <label for="feedback-message">Message</label>
              <textarea 
                id="feedback-message" 
                v-model="feedback.message" 
                placeholder="Please share your thoughts, suggestions, or report issues..." 
                rows="5"
                maxlength="1000"
                required
                aria-describedby="feedback-counter"
              ></textarea>
              <div id="feedback-counter" class="character-counter">
                {{ feedback.message.length }} / 1000 characters
              </div>
            </div>

            <!-- Submit -->
            <div class="form-actions">
              <button type="button" class="cancel-button" @click="closeModal">Cancel</button>
              <button 
                type="submit" 
                class="submit-button" 
                :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Sending...' : 'Send Feedback' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { useAuth } from '#imports';
import { useErrorPopup } from '~/composables/useErrorPopup';
import { useToast } from '~/composables/useToast';

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  }
});

// Emits
const emit = defineEmits(['close', 'submitted']);

// Get current user
const { session } = useAuth();
const userId = session.value?.user?.id;

// Form data
const feedback = reactive({
  category: 'general',
  message: '',
  rating: 0
});

// Form state
const isSubmitting = ref(false);
const { useError } = useErrorPopup();
const { showSuccess, showError } = useToast();

// Close modal
function closeModal() {
  if (!isSubmitting.value) {
    emit('close');
  }
}

// Submit feedback
async function submitFeedback() {
  if (!userId) {
    useError.showError('You must be logged in to submit feedback');
    return;
  }

  isSubmitting.value = true;

  try {
    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        message: feedback.message,
        category: feedback.category,
        rating: feedback.rating || null
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to submit feedback');
    }

    // Reset form
    feedback.message = '';
    feedback.category = 'general';
    feedback.rating = 0;

    // Emit success and close
    emit('submitted', result.feedback);
    emit('close');
    
    // Show success message
    showSuccess('Thanks for your feedback! We appreciate your input.');
  } catch (error) {
    console.error('Error submitting feedback:', error);
    showError(error.message || 'An error occurred while submitting your feedback');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.feedback-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000000;
}

.feedback-modal {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--color-white);
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000001;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
  transform-origin: bottom;
}

.feedback-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.feedback-modal-header h2 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.75rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 0;
  line-height: 1;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--color-text-primary);
}

.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-light-gray);
  border-radius: 4px;
  font-size: 1rem;
}

.rating-container {
  display: flex;
  gap: 0.5rem;
}

.star {
  font-size: 1.75rem;
  color: var(--color-light-gray);
  cursor: pointer;
  transition: color 0.2s;
}

.star:hover,
.star.active {
  color: var(--color-google-yellow); /* Star color when active */
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button,
.submit-button {
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.cancel-button {
  background-color: var(--color-primary-bg-alternate);
  border: 1px solid var(--color-light-gray);
  color: var(--color-text-primary);
}

.submit-button {
  background-color: var(--color-primary-dark);
  border: none;
  color: var(--color-white);
}

.character-counter {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  text-align: right;
}

.cancel-button:hover {
  background-color: var(--color-gray-lightest);
}

.submit-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (min-width: 768px) {
  .feedback-modal {
    bottom: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 500px;
    max-width: 90%;
    border-radius: 8px;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translate(-50%, -40%);
    opacity: 0;
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
  }
}
</style>
